import type { insights } from "@duress/shared";
import { fmtHours, fmtInt, fmtPct } from "@/lib/format";

export interface MetricColumn {
  key: insights.MetricKey;
  label: string;
  short: string;
  lowerIsBetter: boolean;
  format: (v: number) => string;
}

// Metadata (label/format/direction) for every MetricKey -- the source of
// truth IndustryBenchmark.tsx's findColumn() reads from, since that page
// (client detail's "vs Industry Peers" panel) still compares check-in rate
// even though it's not an Industry Insights headline tile.
export const ALL_METRIC_COLUMNS: MetricColumn[] = [
  { key: "network_adoption", label: "Network Adoption", short: "Adoption", lowerIsBetter: false, format: fmtPct },
  { key: "extended_rate", label: "Extended Rate", short: "Extended", lowerIsBetter: true, format: fmtPct },
  { key: "productive_hours", label: "Productive Hours", short: "Prod Hrs", lowerIsBetter: false, format: fmtHours },
  { key: "sessions", label: "Sessions", short: "Sessions", lowerIsBetter: false, format: fmtInt },
  { key: "emergency_activations", label: "Emergency Activations", short: "Emergency", lowerIsBetter: true, format: fmtInt },
  { key: "checkin_rate", label: "Check-in Rate", short: "Check-in", lowerIsBetter: false, format: fmtPct },
];

// Headline tiles, shown on every Industry Insights card and as table columns.
// Check-in Rate is excluded -- not all clients use check-in, so it's a "more
// stats" figure instead (see MORE_STATS below) rather than a tile that's
// blank half the time.
export const COLUMNS: MetricColumn[] = ALL_METRIC_COLUMNS.filter(
  (c) => c.key !== "checkin_rate",
);

export interface MoreStatColumn {
  key: string;
  short: string;
  format: (v: number) => string;
  accessor: (metrics: insights.IndustryRow["metrics"]) => insights.MetricStats | null;
}

// Card-view-only extra figures, tucked behind the "More stats" disclosure --
// less central than the headline tiles, but still worth a look. total_hours
// and team_alerts aren't MetricKeys (see insights.ts): they're not part of
// client benchmarking, only this bucket.
export const MORE_STATS: MoreStatColumn[] = [
  { key: "checkin_rate", short: "Check-in Rate", format: fmtPct, accessor: (m) => m.checkin_rate },
  { key: "total_hours", short: "Total Hours", format: fmtHours, accessor: (m) => m.total_hours },
  { key: "team_alerts", short: "Team Alerts", format: fmtInt, accessor: (m) => m.team_alerts },
];
